import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { 
  ChevronLeft, 
  ChevronRight,
  QrCode,
  ShoppingCart,
  Bell,
  ChefHat,
  Clock,
  CheckCircle,
  CreditCard,
  Sparkles,
  Package,
  TrendingDown,
  ArrowDownCircle,
  ArrowUpCircle
} from 'lucide-react'

const storySteps = [
  {
    id: 1,
    phase: 'Cliente',
    title: 'Cliente chega e escaneia o QR Code',
    description: 'Sem esperar garçom, o cliente acessa o cardápio digital instantaneamente pelo celular. Fotos, descrições e preços atualizados em tempo real.',
    icon: QrCode,
    color: 'bg-blue-500',
    features: ['Cardápio sempre atualizado', 'Fotos de alta qualidade', 'Personalização de pedidos'],
    visual: 'qrcode'
  },
  {
    id: 2,
    phase: 'Pedido',
    title: 'Monta o pedido e envia direto',
    description: 'O cliente seleciona os itens, personaliza (sem cebola, ponto da carne) e envia direto para a cozinha. Sem intermediários, sem erros de anotação.',
    icon: ShoppingCart,
    color: 'bg-emerald-500',
    features: ['Pedido vai direto ao KDS', 'Observações do cliente', 'Carrinho salvo automaticamente'],
    visual: 'cart'
  },
  {
    id: 3,
    phase: 'Garçom',
    title: 'Garçom recebe notificação',
    description: 'O garçom é alertado em tempo real sobre novos pedidos e solicitações. Pode focar no atendimento humanizado enquanto o sistema cuida da operação.',
    icon: Bell,
    color: 'bg-amber-500',
    features: ['Alertas no smartwatch', 'Visão de todas as mesas', 'Priorização automática'],
    visual: 'notification'
  },
  {
    id: 4,
    phase: 'Cozinha',
    title: 'Cozinha recebe no KDS',
    description: 'O pedido aparece automaticamente na tela da cozinha (KDS), organizado por prioridade e tempo. Nada de papéis perdidos ou pedidos esquecidos.',
    icon: ChefHat,
    color: 'bg-orange-500',
    features: ['Tela touch intuitiva', 'Tempo de preparo visível', 'Organização por praça'],
    visual: 'kds'
  },
  {
    id: 5,
    phase: 'Preparo',
    title: 'Preparo com controle de tempo',
    description: 'O cozinheiro marca cada etapa. O sistema controla o SLA e alerta se algo atrasar. Qualidade e agilidade garantidas.',
    icon: Clock,
    color: 'bg-purple-500',
    features: ['Timer automático', 'Alertas de atraso', 'Histórico de produção'],
    visual: 'timer'
  },
  {
    id: 6,
    phase: 'Entrega',
    title: 'Pedido pronto, garçom notificado',
    description: 'Quando finalizado, o garçom recebe alerta instantâneo para buscar o pedido. O cliente acompanha o status pelo celular.',
    icon: CheckCircle,
    color: 'bg-green-500',
    features: ['Notificação push', 'Status em tempo real', 'Rastreio pelo cliente'],
    visual: 'ready'
  },
  {
    id: 7,
    phase: 'Pagamento',
    title: 'Fechamento rápido e fácil',
    description: 'Cliente pede a conta pelo app ou chama o garçom. Divisão de conta, gorjeta opcional, PIX ou cartão - tudo integrado.',
    icon: CreditCard,
    color: 'bg-blue-600',
    features: ['Divisão automática', 'Múltiplas formas', 'Nota fiscal instantânea'],
    visual: 'payment'
  },
  {
    id: 8,
    phase: 'Estoque',
    title: 'Baixa automática de estoque',
    description: 'A cada pedido finalizado, o sistema dá baixa automática nos insumos. Entrada de mercadorias com leitura de código de barras. Controle total, zero desperdício.',
    icon: Package,
    color: 'bg-indigo-600',
    features: ['Baixa automática por receita', 'Entrada com código de barras', 'Alertas de estoque baixo'],
    visual: 'stock'
  },
]

export default function StoryCarousel() {
  const [currentIndex, setCurrentIndex] = useState(0)
  const goToSlide = (index: number) => {
    setCurrentIndex(index)
  }

  const nextSlide = () => {
    setCurrentIndex((prev) => (prev + 1) % storySteps.length)
  }

  const prevSlide = () => {
    setCurrentIndex((prev) => (prev - 1 + storySteps.length) % storySteps.length)
  }

  const currentStep = storySteps[currentIndex]
  const Icon = currentStep.icon

  return (
    <section id="como-funciona" className="py-16 md:py-20 bg-white overflow-hidden relative">
      <div className="container-custom">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-50 border border-blue-100 mb-6">
            <Sparkles className="w-4 h-4 text-blue-500" />
            <span className="text-sm font-medium text-blue-700">Como Funciona</span>
          </div>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-slate-900 mb-4">
            A Jornada Completa do{' '}
            <span className="gradient-text">Seu Cliente</span>
          </h2>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto">
            Veja como o AVAK transforma cada etapa da experiência, 
            do primeiro clique até o pagamento final.
          </p>
        </motion.div>

        {/* Progress bar */}
        <div className="max-w-4xl mx-auto mb-12">
          <div className="flex items-center justify-between mb-4">
            {storySteps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => goToSlide(index)}
                className="flex flex-col items-center group"
              >
                <div 
                  className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                    index <= currentIndex 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-slate-100 text-slate-400 group-hover:bg-slate-200'
                  }`}
                >
                  {index < currentIndex ? (
                    <CheckCircle className="w-5 h-5" />
                  ) : (
                    <span className="text-sm font-semibold">{index + 1}</span>
                  )}
                </div>
                <span className={`text-xs mt-2 hidden md:block transition-colors ${
                  index === currentIndex ? 'text-blue-600 font-medium' : 'text-slate-400'
                }`}>
                  {step.phase}
                </span>
              </button>
            ))}
          </div>
          {/* Progress line */}
          <div className="h-1 bg-slate-100 rounded-full overflow-hidden">
            <motion.div 
              className="h-full bg-blue-600 rounded-full"
              initial={{ width: '0%' }}
              animate={{ width: `${((currentIndex + 1) / storySteps.length) * 100}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>

        {/* Main Carousel Card */}
        <div className="max-w-5xl mx-auto relative">
          {/* Navigation buttons - FORA do card */}
          <button
            onClick={prevSlide}
            className="absolute -left-6 md:-left-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-600 hover:text-blue-600 hover:scale-110 transition-all z-10"
          >
            <ChevronLeft className="w-6 h-6" />
          </button>
          <button
            onClick={nextSlide}
            className="absolute -right-6 md:-right-16 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full bg-white shadow-lg flex items-center justify-center text-slate-600 hover:text-blue-600 hover:scale-110 transition-all z-10"
          >
            <ChevronRight className="w-6 h-6" />
          </button>

          <div className="bg-gradient-to-br from-slate-50 to-blue-50/50 rounded-3xl p-8 md:p-12 border border-slate-100">

            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.4 }}
                className="grid md:grid-cols-2 gap-10 items-center"
              >
                {/* Content */}
                <div className="order-2 md:order-1">
                  <div className="flex items-center gap-3 mb-4">
                    <div className={`w-12 h-12 rounded-xl ${currentStep.color} flex items-center justify-center`}>
                      <Icon className="w-6 h-6 text-white" />
                    </div>
                    <div className="text-sm font-medium text-slate-400">
                      Passo {currentIndex + 1} de {storySteps.length}
                    </div>
                  </div>

                  <h3 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4">
                    {currentStep.title}
                  </h3>

                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {currentStep.description}
                  </p>

                  <ul className="space-y-3">
                    {currentStep.features.map((feature, i) => (
                      <motion.li
                        key={i}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: i * 0.1 }}
                        className="flex items-center gap-3 text-slate-700"
                      >
                        <div className="w-5 h-5 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                          <CheckCircle className="w-3 h-3 text-blue-600" />
                        </div>
                        {feature}
                      </motion.li>
                    ))}
                  </ul>
                </div>

                {/* Visual */}
                <div className="order-1 md:order-2">
                  <StoryVisual step={currentStep} />
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>

        {/* Dots navigation (mobile) */}
        <div className="flex justify-center gap-2 mt-8 md:hidden">
          {storySteps.map((_, index) => (
            <button
              key={index}
              onClick={() => goToSlide(index)}
              className={`w-2 h-2 rounded-full transition-all ${
                index === currentIndex ? 'w-8 bg-blue-600' : 'bg-slate-300'
              }`}
            />
          ))}
        </div>

        {/* Indicador de mais conteúdo */}
        <motion.div 
          className="flex justify-center mt-12"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
        >
          <a href="#modulos" className="flex flex-col items-center gap-2 text-slate-400 hover:text-blue-600 transition-colors group">
            <span className="text-sm font-medium">Conheça os módulos</span>
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.5, repeat: Infinity }}
            >
              <ChevronRight className="w-5 h-5 rotate-90" />
            </motion.div>
          </a>
        </motion.div>
      </div>
    </section>
  )
}

function StoryVisual({ step }: { step: typeof storySteps[0] }) {
  return (
    <motion.div
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="relative"
    >
      {/* Phone mockup */}
      <div className="relative mx-auto w-64">
        <div className="bg-slate-800 rounded-[2.5rem] p-2 shadow-2xl">
          <div className="bg-white rounded-[2rem] overflow-hidden">
            {/* Phone content based on step */}
            <div className="p-4 min-h-[280px] flex flex-col">
              {/* Header */}
              <div className={`text-center py-3 px-4 rounded-xl mb-4 ${step.color} bg-opacity-10`}>
                <step.icon className={`w-8 h-8 mx-auto mb-2 ${step.color.replace('bg-', 'text-')}`} />
                <div className="text-sm font-semibold text-slate-700">{step.phase}</div>
              </div>

              {/* Visual content */}
              <div className="flex-1 flex items-center justify-center">
                {step.visual === 'qrcode' && (
                  <div className="text-center">
                    <div className="w-32 h-32 mx-auto bg-slate-100 rounded-xl flex items-center justify-center mb-3">
                      <QrCode className="w-20 h-20 text-slate-400" />
                    </div>
                    <div className="text-xs text-slate-500">Escaneie para acessar</div>
                  </div>
                )}
                {step.visual === 'cart' && (
                  <div className="w-full space-y-2">
                    {['Hambúrguer Clássico', 'Batata Frita', 'Refrigerante'].map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-slate-50 rounded-lg">
                        <div className="w-10 h-10 bg-slate-200 rounded-lg" />
                        <div className="flex-1">
                          <div className="text-sm font-medium text-slate-700">{item}</div>
                          <div className="text-xs text-slate-400">1x</div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                {step.visual === 'notification' && (
                  <div className="w-full space-y-3">
                    <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Bell className="w-4 h-4 text-blue-600" />
                        <span className="text-xs font-medium text-blue-600">Novo Pedido</span>
                      </div>
                      <div className="text-sm text-slate-700">Mesa 7 - 3 itens</div>
                    </div>
                    <div className="p-3 bg-amber-50 rounded-xl border border-amber-100">
                      <div className="flex items-center gap-2 mb-1">
                        <Clock className="w-4 h-4 text-amber-600" />
                        <span className="text-xs font-medium text-amber-600">Chamado</span>
                      </div>
                      <div className="text-sm text-slate-700">Mesa 4 solicita conta</div>
                    </div>
                  </div>
                )}
                {step.visual === 'kds' && (
                  <div className="w-full grid grid-cols-2 gap-2">
                    {[
                      { status: 'Novo', color: 'bg-blue-500' },
                      { status: 'Fazendo', color: 'bg-amber-500' },
                      { status: 'Fazendo', color: 'bg-amber-500' },
                      { status: 'Pronto', color: 'bg-green-500' },
                    ].map((item, i) => (
                      <div key={i} className="p-2 bg-slate-800 rounded-lg">
                        <div className={`text-[10px] px-2 py-0.5 rounded ${item.color} text-white inline-block mb-1`}>
                          {item.status}
                        </div>
                        <div className="text-xs text-white">Mesa {i + 1}</div>
                      </div>
                    ))}
                  </div>
                )}
                {step.visual === 'timer' && (
                  <div className="text-center">
                    <div className="w-24 h-24 mx-auto rounded-full border-4 border-blue-500 flex items-center justify-center mb-3">
                      <div>
                        <div className="text-2xl font-bold text-slate-900">5:32</div>
                        <div className="text-xs text-slate-500">min</div>
                      </div>
                    </div>
                    <div className="text-sm text-green-600 font-medium">No prazo ✓</div>
                  </div>
                )}
                {step.visual === 'ready' && (
                  <div className="text-center">
                    <motion.div
                      animate={{ scale: [1, 1.1, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                      className="w-20 h-20 mx-auto rounded-full bg-green-100 flex items-center justify-center mb-3"
                    >
                      <CheckCircle className="w-10 h-10 text-green-600" />
                    </motion.div>
                    <div className="text-lg font-semibold text-slate-900">Pedido Pronto!</div>
                    <div className="text-sm text-slate-500">Mesa 7</div>
                  </div>
                )}
                {step.visual === 'payment' && (
                  <div className="w-full space-y-3">
                    <div className="p-3 bg-slate-50 rounded-xl">
                      <div className="flex justify-between mb-2">
                        <span className="text-sm text-slate-600">Subtotal</span>
                        <span className="text-sm font-medium">R$ 89,90</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-sm text-slate-600">Serviço (10%)</span>
                        <span className="text-sm font-medium">R$ 8,99</span>
                      </div>
                    </div>
                    <div className="p-3 bg-blue-50 rounded-xl border border-blue-100">
                      <div className="flex justify-between">
                        <span className="text-sm font-semibold text-slate-900">Total</span>
                        <span className="text-lg font-bold text-blue-600">R$ 98,89</span>
                      </div>
                    </div>
                  </div>
                )}
                {step.visual === 'stock' && (
                  <div className="w-full space-y-2">
                    <div className="p-2 bg-green-50 rounded-xl border border-green-100">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowUpCircle className="w-4 h-4 text-green-600" />
                        <span className="text-xs font-medium text-green-700">Entrada</span>
                      </div>
                      <div className="text-sm text-slate-700">+50 un. Carne Bovina</div>
                      <div className="text-xs text-slate-500">Via código de barras</div>
                    </div>
                    <div className="p-2 bg-red-50 rounded-xl border border-red-100">
                      <div className="flex items-center gap-2 mb-1">
                        <ArrowDownCircle className="w-4 h-4 text-red-500" />
                        <span className="text-xs font-medium text-red-600">Baixa Auto</span>
                      </div>
                      <div className="text-sm text-slate-700">-2 un. Hambúrguer</div>
                      <div className="text-xs text-slate-500">Pedido #1247</div>
                    </div>
                    <div className="p-2 bg-amber-50 rounded-xl border border-amber-100">
                      <div className="flex items-center gap-2">
                        <TrendingDown className="w-4 h-4 text-amber-600" />
                        <span className="text-xs font-medium text-amber-700">Estoque baixo: Queijo</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Glow effect */}
        <div className={`absolute -inset-4 ${step.color} opacity-20 blur-3xl rounded-full -z-10`} />
      </div>
    </motion.div>
  )
}
